import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Radio,
    RadioGroup,
    Select,
} from '@material-ui/core';
import { CustomTextField } from '../../../shared/components/common/customTextField';
import { Modal, PrimaryButton, SecondaryButton } from '../../../shared/ui';
import {
    MAINTENANCE_TYPE,
    getMaintenanceApiTypeLabel,
} from '../../../shared/utils/maintenanceTypeHelpers';
import {
    ChipList,
    FieldCol,
    FieldErrorText,
    FieldLabel,
    ModalBody,
    ModalFooterButtons,
    ModalFormGrid,
    ObservationsHint,
    RadioRow,
} from '../styles';

function resolveMaintenanceType(maintenanceLocalization) {
    return maintenanceLocalization === 'empresa'
        ? MAINTENANCE_TYPE.INCOMPANY
        : MAINTENANCE_TYPE.OFICINA;
}

function ClientQuoteEstimateModalView({
    open,
    service,
    formState,
    availableTasks,
    isEditMode,
    loading,
    currentLanguage,
    onClose,
    onSave,
    onFieldChange,
}) {
    const clientLanguage = currentLanguage.bepiService.client;
    const sharedLanguage = currentLanguage.bepiService.shared;
    const [formErrors, setFormErrors] = useState({});
    const currentServiceId = service?.id;

    useEffect(() => {
        if (!open) return;
        setFormErrors({});
    }, [currentServiceId, isEditMode, open]);

    const modalTitle = useMemo(() => {
        const prefix = isEditMode
            ? clientLanguage.client_quote_modal_edit_title
            : clientLanguage.client_quote_modal_add_title;

        return `${prefix}${service?.nome || service?.descricao || clientLanguage.client_quote_modal_default_service}`;
    }, [clientLanguage, isEditMode, service]);

    const validateForm = useCallback(() => {
        const errors = {};

        if (!formState.quantity || Number(formState.quantity) < 1) {
            errors.quantity = clientLanguage.client_quote_modal_invalid_qty;
        }

        if (!String(formState.model || '').trim()) {
            errors.model = clientLanguage.client_quote_modal_model_required;
        }

        if (!String(formState.brand || '').trim()) {
            errors.brand = clientLanguage.client_quote_modal_brand_required;
        }

        if (!String(formState.problem || '').trim()) {
            errors.problem = clientLanguage.client_quote_modal_problem_required;
        }

        if (!formState.maintenanceLocalization) {
            errors.maintenanceLocalization = clientLanguage.client_quote_modal_location_required;
        }

        if (
            formState.maintenanceLocalization === 'empresa' &&
            !String(formState.companyAddress || '').trim()
        ) {
            errors.companyAddress = clientLanguage.client_quote_modal_address_required;
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [clientLanguage, formState]);

    const handleSave = useCallback(() => {
        if (!validateForm()) return;

        onSave({
            quantity: Number(formState.quantity),
            model: formState.model,
            brand: formState.brand,
            problem: formState.problem,
            observations: formState.observations,
            maintenanceLocalization: formState.maintenanceLocalization,
            tipoManutencao: getMaintenanceApiTypeLabel(resolveMaintenanceType(formState.maintenanceLocalization)),
            companyAddress: formState.companyAddress,
            selectedTasks: formState.selectedTasks,
        });
    }, [formState, onSave, validateForm]);

    const footer = (
        <ModalFooterButtons>
            <SecondaryButton type='button' onClick={onClose}>
                {sharedLanguage.shared_details_cancel_btn}
            </SecondaryButton>
            <PrimaryButton type='button' onClick={handleSave} disabled={loading}>
                {loading
                    ? sharedLanguage.shared_equip_form_saving_btn
                    : isEditMode
                        ? sharedLanguage.shared_clients_save_changes_btn
                        : clientLanguage.client_quote_modal_add_to_quote_btn}
            </PrimaryButton>
        </ModalFooterButtons>
    );

    return (
        <Modal
            isOpen={open}
            type='info'
            title={modalTitle}
            onCancel={onClose}
            footer={footer}
            showIcon={false}
            align='left'
            maxWidth='760px'
        >
            <ModalBody>
                <ModalFormGrid>
                    <FieldCol $span={4}>
                        <FieldLabel $error={Boolean(formErrors.quantity)}>
                            {sharedLanguage.shared_estimate_respond_qty_label} *
                        </FieldLabel>
                        <CustomTextField
                            type='number'
                            value={formState.quantity}
                            onChange={(event) => onFieldChange('quantity', Number(event.target.value))}
                            fullWidth
                            inputProps={{ min: 1 }}
                            size='small'
                            error={Boolean(formErrors.quantity)}
                        />
                        {formErrors.quantity ? <FieldErrorText>{formErrors.quantity}</FieldErrorText> : null}
                    </FieldCol>

                    <FieldCol $span={4}>
                        <FieldLabel $error={Boolean(formErrors.model)}>
                            {sharedLanguage.shared_equip_form_model_label} *
                        </FieldLabel>
                        <CustomTextField
                            value={formState.model}
                            onChange={(event) => onFieldChange('model', event.target.value)}
                            fullWidth
                            size='small'
                            error={Boolean(formErrors.model)}
                        />
                        {formErrors.model ? <FieldErrorText>{formErrors.model}</FieldErrorText> : null}
                    </FieldCol>

                    <FieldCol $span={4}>
                        <FieldLabel $error={Boolean(formErrors.brand)}>
                            {sharedLanguage.shared_equip_form_brand_label} *
                        </FieldLabel>
                        <CustomTextField
                            value={formState.brand}
                            onChange={(event) => onFieldChange('brand', event.target.value)}
                            fullWidth
                            size='small'
                            error={Boolean(formErrors.brand)}
                        />
                        {formErrors.brand ? <FieldErrorText>{formErrors.brand}</FieldErrorText> : null}
                    </FieldCol>

                    <FieldCol $span={12}>
                        <FieldLabel $error={Boolean(formErrors.problem)}>
                            {clientLanguage.client_quote_modal_problem_label} *
                        </FieldLabel>
                        <CustomTextField
                            value={formState.problem}
                            onChange={(event) => onFieldChange('problem', event.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            size='small'
                            error={Boolean(formErrors.problem)}
                            placeholder={clientLanguage.client_quote_modal_problem_placeholder}
                        />
                        {formErrors.problem ? <FieldErrorText>{formErrors.problem}</FieldErrorText> : null}
                    </FieldCol>

                    <FieldCol $span={12}>
                        <FieldLabel $error={Boolean(formErrors.maintenanceLocalization)}>
                            {clientLanguage.client_quote_modal_location_label} *
                        </FieldLabel>
                        <FormControl component='fieldset' fullWidth>
                            <RadioGroup
                                value={formState.maintenanceLocalization}
                                onChange={(event) => onFieldChange('maintenanceLocalization', event.target.value)}
                            >
                                <RadioRow>
                                    <FormControlLabel
                                        value='oficina'
                                        control={<Radio color='primary' />}
                                        label={clientLanguage.client_quote_modal_in_oficina_radio}
                                    />
                                    <FormControlLabel
                                        value='empresa'
                                        control={<Radio color='primary' />}
                                        label={clientLanguage.client_quote_modal_in_empresa_radio}
                                    />
                                </RadioRow>
                            </RadioGroup>
                        </FormControl>
                        {formErrors.maintenanceLocalization ? (
                            <FieldErrorText>{formErrors.maintenanceLocalization}</FieldErrorText>
                        ) : null}
                    </FieldCol>

                    {formState.maintenanceLocalization === 'empresa' ? (
                        <FieldCol $span={12}>
                            <FieldLabel $error={Boolean(formErrors.companyAddress)}>
                                {clientLanguage.client_quote_modal_address_label} *
                            </FieldLabel>
                            <CustomTextField
                                value={formState.companyAddress}
                                onChange={(event) => onFieldChange('companyAddress', event.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                size='small'
                                error={Boolean(formErrors.companyAddress)}
                                placeholder={clientLanguage.client_quote_modal_address_placeholder}
                            />
                            {formErrors.companyAddress ? (
                                <FieldErrorText>{formErrors.companyAddress}</FieldErrorText>
                            ) : null}
                        </FieldCol>
                    ) : null}

                    <FieldCol $span={12}>
                        <FieldLabel>Dados Selecionados</FieldLabel>
                        <div style={{ padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Categoria: </span>
                                <span style={{ color: '#4B5563', fontSize: '0.9rem' }}>{service?.categoria_nome || '-'}</span>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Produto/Família: </span>
                                <span style={{ color: '#4B5563', fontSize: '0.9rem' }}>{service?.produto_nome || '-'}</span>
                            </div>
                            <div>
                                <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Serviço/Especificação: </span>
                                <span style={{ color: '#4B5563', fontSize: '0.9rem' }}>{service?.nome || '-'}</span>
                            </div>
                        </div>
                    </FieldCol>

                    <FieldCol $span={12}>
                        <FieldLabel>
                            {isEditMode
                                ? clientLanguage.client_quote_modal_obs_edit_label
                                : clientLanguage.client_quote_modal_obs_auto_label}
                        </FieldLabel>
                        <CustomTextField
                            value={formState.observations}
                            fullWidth
                            multiline
                            rows={2}
                            disabled={!isEditMode}
                            variant={isEditMode ? 'outlined' : 'filled'}
                            size='small'
                            onChange={isEditMode ? (event) => onFieldChange('observations', event.target.value) : undefined}
                        />
                        <ObservationsHint>{clientLanguage.client_quote_modal_obs_helper}</ObservationsHint>
                    </FieldCol>
                </ModalFormGrid>
            </ModalBody>
        </Modal>
    );
}

ClientQuoteEstimateModalView.propTypes = {
    open: PropTypes.bool.isRequired,
    service: PropTypes.object,
    formState: PropTypes.shape({
        quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        model: PropTypes.string,
        brand: PropTypes.string,
        problem: PropTypes.string,
        observations: PropTypes.string,
        maintenanceLocalization: PropTypes.string,
        companyAddress: PropTypes.string,
        selectedTasks: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object])
        ),
    }).isRequired,
    availableTasks: PropTypes.arrayOf(PropTypes.object),
    isEditMode: PropTypes.bool,
    loading: PropTypes.bool,
    currentLanguage: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
};

ClientQuoteEstimateModalView.defaultProps = {
    service: null,
    availableTasks: [],
    isEditMode: false,
    loading: false,
};

export default ClientQuoteEstimateModalView;
